import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { computedAsync, isDefined } from '@vueuse/core';
import { cloneDeep, isEqual, last } from 'lodash';

import { adaptRecipeIngredientsToItinerarySearchCriteria } from '@apps/main/modules/criteria/itinerarySearchCriteria/itinerariesCriteria';
import {
  getActivityAvailabilitySearchDates,
  getActivityDetailsPriceCriteria,
  getSearchMetaData,
  hasChangedOnlySelectedDate,
} from '@apps/main/new-desktop/core/stores/criteria/criteriaStore.logic';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
  isOwnStayIngredient,
  isStayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { DestinationAvailabilityRecipe } from '@apps/main/modules/criteria/destinationAvailabilitySearchCriteria/destinationAvialabilityCriteria';
import { adaptRecipeToDestinationAvailabilitySearchCriteria } from '@apps/main/modules/criteria/destinationAvailabilitySearchCriteria/destinationAvialabilityCriteria';
import { getBaseConstraintQL } from '@apps/main/modules/criteria/ingredientAdapters/baseIngredientAdapter';
import { adaptHotelIngredientToHotelConstraintQL } from '@apps/main/modules/criteria/ingredientAdapters/activitiesInDestinationSearchAdapter/activitiesInDestinationSearchAdapter';

import {
  AutoCompleteSearchType,
  ProductSearchType,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import type {
  AccommodationAvailabilityUserCriteria,
  ActivityDetailsPriceCriteria,
  HeartedHotel,
  ActivitiesInDestinationCriteria,
  ActivityAvailabilityUserCriteria,
  ActivitySuggestionsSearchCriteria,
  BusinessCriteria,
  DestinationAvailabilitySearchUserCriteria,
  DestinationSuggestedActivitiesSearchCriteria,
  DestinationSuggestedActivitySearchCriteria,
  HotelConstraintSelectionQL,
  ItinerarySearchCriteria,
  ItinerarySearchUserCriteria,
  ItinerarySearchUserCriteriaDependencies,
  SearchSuggestionCriteria,
  TransportAvailabilityUserCriteria,
} from '@libs/trip-planner-interfaces';
import { isMultiExperimentAsync } from '@libs/feature-toggle';
import { readonlyRef } from '@libs/utils';

import { useTripStore } from '../trip/tripStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useMetaStore } from '../meta/metaStore';
import type { DateWithItinerary } from '../trip/tripStore.model';
import { useProductDetailStore } from '../productDetail/productDetailStore';
import { useEditingIngredientStore } from '../editingIngredient/editingIngredientStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import {
  getEndSearchDateFromPreferredDay,
  getStartSearchDateFromPreferredDay,
} from '../preferredTimeStore/preferredTimeStoreLogic';
import { usePreferredTimeStore } from '../preferredTimeStore';
import { usePaxStore } from '../pax/paxStore';
import { useCustomerFlowStore } from '../customerFlowStore';
import { useRouterStore } from '../routerStore';
import { useBrowserStore } from '../browserStore';
import { TripPlannerPage } from '../../models';

import {
  getHotelSelectionListLengthExperimentName,
  getNumberOfHotelSuggestions,
} from './hotelSelectionListLengthExperiment';

export interface DatesToSearch {
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
}

export const useCriteriaStore = defineStore('CriteriaStore', () => {
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();
  const preferredTimeStore = usePreferredTimeStore();
  const metaStore = useMetaStore();
  const itineraryStore = useItineraryStore();
  const editingIngredientStore = useEditingIngredientStore();
  const productDetailStore = useProductDetailStore();
  const paxStore = usePaxStore();
  const customerFlowStore = useCustomerFlowStore();
  const routerStore = useRouterStore();
  const browserStore = useBrowserStore();

  const hotelSelectionListLengthExperimentName =
    getHotelSelectionListLengthExperimentName(browserStore.isSmallerThan1200px);
  const hotelSelectionListLengthVariant = computedAsync(() =>
    isMultiExperimentAsync(hotelSelectionListLengthExperimentName)
  );

  const shouldSkipCacheForNextTripFetch = ref(false);

  const itineraryCriteriaDependencies =
    computed<ItinerarySearchUserCriteriaDependencies>(() => {
      const skipCache = shouldSkipCacheForNextTripFetch.value;

      return {
        ingredients: adaptRecipeIngredientsToItinerarySearchCriteria(
          recipeStore.recipe
        ),
        pax: paxStore.pax.rooms,
        startSearchDate: preferredTimeStore.startSearchDate,
        endSearchDate: preferredTimeStore.endSearchDate,
        selectedDate: preferredTimeStore.preferredDay,
        skipCache,
      };
    });

  const itineraryCriteria = ref<ItinerarySearchUserCriteria>({
    ...itineraryCriteriaDependencies.value,
    sessionId: tripStore.sessionId,
  });

  const businessCriteria = computed<BusinessCriteria>(() => ({
    affiliateId: metaStore.metaAffiliateId,
    metaData: getSearchMetaData(recipeStore.metaData, navigator.userAgent),
    searchForPackageRates: recipeStore.searchForPackageRates,
    avoidDateShift:
      customerFlowStore.isFlightOnlyFromMarketplaceFlow &&
      recipeStore.areAllBookableProductsFlights,
    searchOnlyLiveSelectedDay:
      customerFlowStore.isFlightOnlyFromMarketplaceFlow ||
      customerFlowStore.isMetaHotelFlow,
  }));

  const searchSuggestionCriteria = ref<SearchSuggestionCriteria>({
    searchText: '',
    searchType: AutoCompleteSearchType.Destination,
  });

  watch(
    itineraryCriteriaDependencies,
    (dependencies, oldDependencies) => {
      const isSearchedItinerary =
        itineraryStore.userSelectedItinerary?.isSearched;
      const isSearchingAvailability =
        itineraryStore.isSearchingItinerariesAvailability;
      const hasUserSelectedItinerary = isDefined(
        itineraryStore.userSelectedItinerary
      );

      if (
        (isSearchedItinerary ||
          (isSearchingAvailability && !hasUserSelectedItinerary)) &&
        hasChangedOnlySelectedDate(dependencies, oldDependencies)
      ) {
        return;
      }

      const selectedDate =
        isDefined(itineraryStore.userSelectedItinerary) &&
        !itineraryStore.userSelectedItinerary.isSearched
          ? dependencies.selectedDate
          : undefined;

      const finalStartDate =
        selectedDate ??
        (preferredTimeStore.preferredDay
          ? getStartSearchDateFromPreferredDay(
              preferredTimeStore.preferredDay,
              new Date()
            )
          : dependencies.startSearchDate);
      const finalEndDate =
        selectedDate ??
        (preferredTimeStore.preferredDay
          ? getEndSearchDateFromPreferredDay(preferredTimeStore.preferredDay)
          : dependencies.endSearchDate);

      const newItineraryCriteria: ItinerarySearchUserCriteria = {
        ...dependencies,
        endSearchDate: finalEndDate,
        startSearchDate: finalStartDate,
        sessionId: tripStore.sessionId,
      };

      const isSameCriteria = isEqual(
        itineraryCriteria.value,
        newItineraryCriteria
      );

      if (isSameCriteria) {
        return;
      }

      if (
        hasChangedOnlySelectedDate(
          newItineraryCriteria,
          itineraryCriteria.value
        ) &&
        itineraryStore.areWholeMonthItinerariesSearched
      ) {
        return;
      }

      itineraryCriteria.value = newItineraryCriteria;

      if (shouldSkipCacheForNextTripFetch.value) {
        shouldSkipCacheForNextTripFetch.value = false;
      }
    },
    { deep: true }
  );

  function getDatesToSearch(
    ingredient: StayIngredient,
    preferredDay?: Date
  ): DatesToSearch {
    if (!preferredDay) {
      return getDaysInMonthFromItineraryDates(itineraryStore.itineraryDates);
    }

    return getDaysWithSelectedDate(ingredient);
  }

  function getDaysWithSelectedDate(ingredient: StayIngredient): DatesToSearch {
    let ingredientDate = itineraryStore.getIngredientDate(
      ingredient.ingredientId
    );

    if (!ingredientDate && preferredTimeStore.preferredDay) {
      ingredientDate = recipeStore.getIngredientDateInRecipe(
        ingredient.ingredientId,
        preferredTimeStore.preferredDay
      );
    }

    if (!ingredientDate) {
      throw new Error('No date available on provided ingredient');
    }

    const monthDates = getDaysInMonthFromItineraryDates(
      itineraryStore.itineraryDates
    );

    return {
      startDate: monthDates.startDate,
      endDate: monthDates.endDate,
      selectedDate: ingredientDate,
    };
  }

  function getDaysInMonthFromItineraryDates(
    itineraryDates: DateWithItinerary[]
  ): DatesToSearch {
    const firstMonthDate = itineraryDates[0]?.date;
    const lastMonthDate = last(itineraryDates)?.date;

    if (!firstMonthDate || !lastMonthDate) {
      return {};
    }

    return {
      startDate: firstMonthDate,
      endDate: lastMonthDate,
    };
  }

  function skipCacheForNextTripFetch(): void {
    shouldSkipCacheForNextTripFetch.value = true;
  }

  const activitySuggestionsCriteria = computed<
    ActivitySuggestionsSearchCriteria[]
  >(() => {
    const searchDate = itineraryStore.tripStartDate;

    if (!searchDate) {
      return [];
    }

    return recipeStore.stayOnlyRecipe.reduce(
      (acc: ActivitySuggestionsSearchCriteria[], ingredient, index: number) => {
        const constraint: HotelConstraintSelectionQL = getBaseConstraintQL(
          ingredient.searchTerm
        );

        const criteria = cloneDeep(itineraryCriteria.value);
        const ingredientFromCriteria = criteria.ingredients
          ? criteria.ingredients[index]
          : null;

        if (
          ingredientFromCriteria &&
          (isHotelIngredientAdapter(ingredientFromCriteria) ||
            isOwnStayIngredientAdapter(ingredientFromCriteria))
        ) {
          ingredientFromCriteria.constraint.selectedHotelTypes = [
            SelectedHotelTypeQL.Golf,
            SelectedHotelTypeQL.Ticket,
          ];
        }
        acc.push({
          userCriteria: {
            recipe: criteria,
            ingredientIndex: index,
            day: searchDate,
            constraint,
          },
          businessCriteria: businessCriteria.value,
        });

        return acc;
      },
      []
    );
  });

  const destinationAvailabilitySearchUserCriteria = computed<
    DestinationAvailabilitySearchUserCriteria | undefined
  >(() => {
    const currentPage = routerStore.getCurrentTripPlannerView();

    if (
      !currentPage ||
      ![
        TripPlannerPage.HotelSelection,
        TripPlannerPage.HotelDetailsInHotelSelection,
        TripPlannerPage.ActivitySelection,
        TripPlannerPage.ActivityDetailsInActivitySelection,
      ].includes(currentPage)
    ) {
      return;
    }

    // Hotel selection gets its products from search-itineraries. Destination
    // availability remains responsible for activity-selection products only.
    const isHotelSelectionFlow =
      currentPage === TripPlannerPage.HotelSelection ||
      currentPage === TripPlannerPage.HotelDetailsInHotelSelection;
    if (isHotelSelectionFlow) {
      return;
    }

    const destinationIngredient = cloneDeep(
      editingIngredientStore.editingStayIngredient
    );

    if (!destinationIngredient || destinationIngredient.isNewIngredient) {
      return;
    }

    let datesToSearch: DatesToSearch;

    try {
      datesToSearch = getDatesToSearch(
        destinationIngredient,
        preferredTimeStore.preferredDay
      );
    } catch {
      return;
    }

    const destinationAvailabilityRecipe: DestinationAvailabilityRecipe = {
      destinationIngredient,
      itineraryCriteria: itineraryCriteria.value,
      searchActivities: recipeStore.searchActivities,
      datesToSearch,
    };

    return adaptRecipeToDestinationAvailabilitySearchCriteria(
      destinationAvailabilityRecipe
    );
  });

  const hotelSelectionItinerarySearchCriteria = computed<
    ItinerarySearchCriteria | undefined
  >(() => {
    const currentPage = routerStore.getCurrentTripPlannerView();
    const isHotelSelectionFlow =
      currentPage === TripPlannerPage.HotelSelection ||
      currentPage === TripPlannerPage.HotelDetailsInHotelSelection;
    const selectedDate = preferredTimeStore.preferredDay;
    const editingIngredient = editingIngredientStore.editingStayIngredient;

    if (
      !isHotelSelectionFlow ||
      !selectedDate ||
      !editingIngredient ||
      !hotelSelectionListLengthVariant.value
    ) {
      return;
    }

    const ingredients = adaptRecipeIngredientsToItinerarySearchCriteria(
      recipeStore.recipe
    );

    return {
      userCriteria: {
        ingredients,
        pax: paxStore.pax.rooms,
        startSearchDate: selectedDate,
        endSearchDate: selectedDate,
        selectedDate,
        sessionId: tripStore.sessionId,
      },
      businessCriteria: businessCriteria.value,
      numberOfHotelSuggestions: getNumberOfHotelSuggestions(
        hotelSelectionListLengthVariant.value
      ),
      isHotelSelectionSearch: true,
    };
  });

  const activityProductAvailabilityCriteria = computed<
    ActivityAvailabilityUserCriteria | undefined
  >(() => {
    const productCode = productDetailStore.destinationProductDetailCode;
    const ingredientId =
      productDetailStore.destinationProductDetailIngredientId;
    const isActivityProduct =
      productDetailStore.productSearchType === ProductSearchType.Activity;

    if (!productCode || !ingredientId || !isActivityProduct) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const parentIngredientId = ingredientResponse.ingredientId;

    const ingredientIndex =
      recipeStore.recipeWithoutUnknownOwnTransport.findIndex(
        (ingredient) => ingredient.ingredientId === parentIngredientId
      );

    if (ingredientIndex === -1) {
      return;
    }

    const ingredient = recipeStore.getStayIngredient(parentIngredientId);

    const activityAvailabilitySearchDates = getActivityAvailabilitySearchDates(
      getDatesToSearch(ingredient, preferredTimeStore.preferredDay),
      ingredientResponse.numberOfNights
    );

    const recipeWithDates: ItinerarySearchUserCriteria = {
      ...itineraryCriteria.value,
      startSearchDate: activityAvailabilitySearchDates.startDate,
      endSearchDate: activityAvailabilitySearchDates.endDate,
      selectedDate: activityAvailabilitySearchDates.selectedDate,
    };

    return {
      productCode,
      parentIngredientId,
      recipe: recipeWithDates,
      ingredientIndex,
    };
  });

  const activityDetailsFromPriceCriteria = computed<
    ActivityDetailsPriceCriteria | undefined
  >(() => {
    const productCode = productDetailStore.destinationProductDetailCode;
    const ingredientId =
      productDetailStore.destinationProductDetailIngredientId;
    const isActivityProduct =
      productDetailStore.productSearchType === ProductSearchType.Activity;

    if (!productCode || !ingredientId || !isActivityProduct) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const datesToSearch = {
      ...getDaysInMonthFromItineraryDates(itineraryStore.itineraryDates),
      selectedDate: preferredTimeStore.preferredDay
        ? ingredientResponse.date
        : undefined,
    };

    return getActivityDetailsPriceCriteria({
      businessCriteria: businessCriteria.value,
      productCode,
      numberOfNights: ingredientResponse.numberOfNights,
      datesToSearch,
      sessionId: itineraryCriteria.value.sessionId,
      skipCache: itineraryCriteria.value.skipCache,
    });
  });

  const destinationSuggestedActivitiesCriteria =
    computed<DestinationSuggestedActivitiesSearchCriteria>(() => {
      const NUMBER_OF_SUGGESTIONS = 3;
      const destinations: DestinationSuggestedActivitySearchCriteria[] =
        recipeStore.recipe.reduce(
          (
            suggestedActivitiesCriterias: DestinationSuggestedActivitySearchCriteria[],
            ingredient
          ) => {
            if (isStayIngredient(ingredient)) {
              suggestedActivitiesCriterias.push({
                constraint: adaptHotelIngredientToHotelConstraintQL(
                  ingredient,
                  NUMBER_OF_SUGGESTIONS
                ),
                ingredientId: ingredient.ingredientId,
              });
            }

            return suggestedActivitiesCriterias;
          },
          []
        );

      return { destinations };
    });

  const accommodationProductAvailabilityCriteria = computed<
    AccommodationAvailabilityUserCriteria | undefined
  >(() => {
    const productCode = productDetailStore.destinationProductDetailCode;
    const ingredientId =
      productDetailStore.destinationProductDetailIngredientId;
    const isHotelProduct =
      productDetailStore.productSearchType === ProductSearchType.Accommodation;

    if (!productCode || !ingredientId || !isHotelProduct) {
      return;
    }

    const ingredientIndex =
      recipeStore.recipeWithoutUnknownOwnTransport.findIndex(
        (ingredient) => ingredient.ingredientId === ingredientId
      );

    if (ingredientIndex === -1) {
      return;
    }

    const ingredient = recipeStore.getStayIngredient(ingredientId);

    if (productDetailStore.overriddenNumberOfNights !== undefined) {
      ingredient.numberOfNights = productDetailStore.overriddenNumberOfNights;
    }

    const { startDate, endDate, selectedDate } = getDatesToSearch(
      ingredient,
      preferredTimeStore.preferredDay
    );

    const recipeWithDates: ItinerarySearchUserCriteria = {
      ...itineraryCriteria.value,
      startSearchDate: startDate,
      endSearchDate: endDate,
    };

    return {
      recipe: recipeWithDates,
      ingredientIndex,
      productCode,
      day: selectedDate,
    };
  });

  // Injects the meta room into a cloned ingredient only, so the recipe and its
  // heartedHotels stay untouched.
  const metaRoomAvailabilityCriteria = computed<
    AccommodationAvailabilityUserCriteria | undefined
  >(() => {
    const metaHotelCode = metaStore.metaHotelCode;
    const metaRoom = metaStore.metaRoom;

    // The meta fields already self-scope this side-channel search, so it does
    // not need to depend on customer-flow initialization.
    if (!metaHotelCode || !metaRoom?.length) {
      return;
    }

    // Meta flow is single-hotel: the meta hotel arrives as a suggested code, not a
    // HotelCode search term, so it's matched as the only hotel ingredient.
    const hotelIngredients = recipeStore.hotelOnlyRecipe;
    const ingredient =
      hotelIngredients.length === 1 ? hotelIngredients[0] : undefined;

    if (!ingredient) {
      return;
    }

    // Only the meta room itself makes this search redundant. A room picked at
    // another hotel must keep the meta hotel priced on hotel selection.
    if (recipeStore.isRoomSelectedAtHotel(metaHotelCode)) {
      return;
    }

    const ingredientIndex =
      recipeStore.recipeWithoutUnknownOwnTransport.findIndex(
        (recipeIngredient) =>
          recipeIngredient.ingredientId === ingredient.ingredientId
      );

    if (ingredientIndex === -1) {
      return;
    }

    const { startDate, endDate, selectedDate } = getDatesToSearch(
      ingredient,
      preferredTimeStore.preferredDay
    );

    const metaHeartedHotels: HeartedHotel[] = [
      { code: metaHotelCode, roomCodes: metaRoom },
    ];

    const recipeWithMetaRoom: ItinerarySearchUserCriteria = {
      ...itineraryCriteria.value,
      startSearchDate: startDate,
      endSearchDate: endDate,
      ingredients:
        itineraryCriteria.value.ingredients?.map((adaptedIngredient, index) =>
          index === ingredientIndex &&
          isHotelIngredientAdapter(adaptedIngredient)
            ? { ...adaptedIngredient, heartedHotels: metaHeartedHotels }
            : adaptedIngredient
        ) ?? null,
    };

    return {
      recipe: recipeWithMetaRoom,
      ingredientIndex,
      productCode: metaHotelCode,
      day: selectedDate,
    };
  });

  const searchActivitiesInDestinationCriteria = computed<
    ActivitiesInDestinationCriteria[]
  >(() =>
    recipeStore.stayOnlyRecipe.map((stayIngredient) => ({
      ingredientId: stayIngredient.ingredientId,
      searchTerm: stayIngredient.searchTerm,
      nearbyPolygonText: isOwnStayIngredient(stayIngredient)
        ? undefined
        : stayIngredient.nearbyPolygon?.name,
      nearbyPolygonId: isOwnStayIngredient(stayIngredient)
        ? undefined
        : stayIngredient.nearbyPolygon?.id,
    }))
  );

  const transportAvailabilityCriteria = computed<
    TransportAvailabilityUserCriteria | undefined
  >(() => {
    if (
      !editingIngredientStore.editingTransportIngredient ||
      !editingIngredientStore.editingTransportIngredient.isIngredientCorrect()
    ) {
      return;
    }

    if (!itineraryStore.selectedItinerary?.date) {
      return;
    }

    return {
      day: itineraryStore.selectedItinerary.date,
      recipe: itineraryCriteria.value,
      ingredientIndex: recipeStore.recipeWithoutUnknownOwnTransport.indexOf(
        editingIngredientStore.editingTransportIngredient
      ),
      elementsInPage: 200,
      page: 0,
    };
  });

  return {
    itineraryCriteria: readonlyRef(itineraryCriteria),
    businessCriteria: readonlyRef(businessCriteria),
    searchSuggestionCriteria: readonlyRef(searchSuggestionCriteria),

    destinationAvailabilitySearchUserCriteria,
    hotelSelectionItinerarySearchCriteria,
    accommodationProductAvailabilityCriteria,
    metaRoomAvailabilityCriteria,
    activityProductAvailabilityCriteria,
    activityDetailsFromPriceCriteria,
    searchActivitiesInDestinationCriteria,
    transportAvailabilityCriteria,
    activitySuggestionsCriteria,
    destinationSuggestedActivitiesCriteria,
    skipCacheForNextTripFetch,

    getDatesToSearch,
  };
});
